import React, { Fragment } from 'react'
import { Alert, ProgressBar } from "react-bootstrap"
import { TextField } from '@mui/material'

import Constants from '../../config.js'
import { getLog } from '../../helpers/helpers.js'
import { UserContext } from "../../Contexts/UserContext/UserContext.js"
import ClickToOpenCardDark from '../../components/ClickToOpenCardDark.js'
import { SlidePopup, TextCopyQR } from '../../components/WithPopup/TextCopyQR.js'
import { ButtonPrimary, ButtonPrimaryOutlined } from '../../components/Buttons.js'
import { Loader } from '../../components/Loader.js'
import PasswordStrengthMeter from '../../components/PasswordStrengthMeter.js'
import PasswordGenerator from '../../components/PasswordGenerator.js'
import { downloadEncryptedBackupFile } from '../../helpers/backup_file_download.js'
import ButtonParam from './ButtonParam.js'


/**
 * Backup & Password section — combines the seed-phrase reveal (with password
 * gate) and the export / change-password actions into one card.
 *
 * State:
 *   - showmnemonic / showmnemonic_timeleft: timed reveal of the seed phrase
 *   - backup_popup*: popup state for export / change_password / show_seed_password
 */
class SectionBackupPassword extends React.Component {

	static contextType = UserContext

	constructor(props) {
		super(props)
		this.classname = "Slide_Settings/SectionBackupPassword: "

		this.state = {
			showmnemonic: false,
			showmnemonic_timeleft: 0,

			// which popup: null | "export" | "change_password" | "show_seed_password"
			backup_popup: null,
			backup_popup_password: "",
			backup_popup_newpassword: "",
			backup_popup_newpassword_confirm: "",
			backup_popup_error: "",
			backup_popup_loading: false,
			backup_popup_success: "",
		}
		this.showmnemonic_timeshow = 20 * 1000 // in ms

		this.handleClick_showmnemonic = this.handleClick_showmnemonic.bind(this)
		this.reduce_timeleft = this.reduce_timeleft.bind(this)

		this.open_backup_popup = this.open_backup_popup.bind(this)
		this.close_backup_popup = this.close_backup_popup.bind(this)
		this.handleChange_backup_popup_password = this.handleChange_backup_popup_password.bind(this)
		this.handleChange_backup_popup_newpassword = this.handleChange_backup_popup_newpassword.bind(this)
		this.handleChange_backup_popup_newpassword_confirm = this.handleChange_backup_popup_newpassword_confirm.bind(this)
		this.handleConfirm_export = this.handleConfirm_export.bind(this)
		this.handleConfirm_change_password = this.handleConfirm_change_password.bind(this)
		this.handleConfirm_show_seed_password = this.handleConfirm_show_seed_password.bind(this)
	}

	componentDidMount() {
		// If autoOpenChangePassword is true (set by the weak-password banner's
		// "Change password" button via the #change_password hash), auto-open
		// the change-password popup so the user doesn't have to click through
		// the settings card manually.
		if (this.props.autoOpenChangePassword) {
			this.open_backup_popup("change_password")
		}
	}


	async handleClick_showmnemonic() {
		const function_id = this.classname + "handleClick_showmnemonic(): "
		const log = getLog(function_id)

		let abort = true
		let message = "⚠️ ETES VOUS SUR ? PERSONNE DERRIERE VOUS ? ⚠️"
		if (!Constants.IS_WEB_BUILD) {
			const options = {
				type: 'question',
				buttons: ['Continue', 'Cancel'],
				defaultId: 1,
				title: 'Warning',
				message: message
			}
			await window.electron.openDialogShowMessageBox(options).then(async (resdata) => {
				const response = resdata.response
				log("response = " + response)
				if (response === 0) abort = false
			})
		} else {
			let res = window.confirm(message)
			if (res === true) abort = false
		}
		if (abort) return

		// confirmed — now ask for the wallet password before revealing
		this.open_backup_popup("show_seed_password")
	}


	async reduce_timeleft(last_timestamp = null) {
		if (last_timestamp == null) last_timestamp = Date.now()

		if (this.state.showmnemonic_timeleft < 0) {
			this.setState({ showmnemonic: false })
			return
		}
		this.setState({
			showmnemonic_timeleft: this.state.showmnemonic_timeleft - (Date.now() - last_timestamp)
		})
		last_timestamp = Date.now()
		setTimeout(() => { this.reduce_timeleft(last_timestamp) }, 50)
	}


	// --- Backup popup handlers ---

	open_backup_popup(popup_name) {
		this.setState({
			backup_popup: popup_name,
			backup_popup_password: "",
			backup_popup_newpassword: "",
			backup_popup_newpassword_confirm: "",
			backup_popup_error: "",
			backup_popup_loading: false,
			backup_popup_success: "",
		})
	}

	close_backup_popup() {
		this.setState({
			backup_popup: null,
			backup_popup_password: "",
			backup_popup_newpassword: "",
			backup_popup_newpassword_confirm: "",
			backup_popup_error: "",
			backup_popup_loading: false,
			backup_popup_success: "",
		})
	}

	handleChange_backup_popup_password(event) {
		this.setState({ backup_popup_password: event.target.value, backup_popup_error: "" })
	}

	handleChange_backup_popup_newpassword(event) {
		this.setState({ backup_popup_newpassword: event.target.value, backup_popup_error: "" })
	}

	handleChange_backup_popup_newpassword_confirm(event) {
		this.setState({ backup_popup_newpassword_confirm: event.target.value, backup_popup_error: "" })
	}


	async handleConfirm_export() {
		const function_id = this.classname + "handleConfirm_export(): "
		const log = getLog(function_id)

		const password = this.state.backup_popup_password
		if (!password) {
			this.setState({ backup_popup_error: "Please enter your password." })
			return
		}

		this.setState({ backup_popup_loading: true, backup_popup_error: "" })
		await new Promise(resolve => setTimeout(resolve, 200))

		try {
			const ok = await this.context.verifyWalletPassword(password)
			if (!ok) {
				this.setState({ backup_popup_loading: false, backup_popup_error: "Wrong password." })
				return
			}

			const result = await downloadEncryptedBackupFile(
				password,
				this.context.walletname,
				this.context.address_owner,
				(pw) => this.context.generateBackupFile(pw)
			)

			if (!result.success) {
				this.setState({ backup_popup_loading: false, backup_popup_error: result.error || "Download failed." })
				return
			}

			log("export success")
			this.setState({ backup_popup_loading: false, backup_popup_success: "Backup file downloaded successfully." })
		} catch (err) {
			log("export error:", err)
			this.setState({ backup_popup_loading: false, backup_popup_error: String(err && err.message ? err.message : err) })
		}
	}


	async handleConfirm_change_password() {
		const function_id = this.classname + "handleConfirm_change_password(): "
		const log = getLog(function_id)

		const oldPassword = this.state.backup_popup_password
		const newPassword = this.state.backup_popup_newpassword
		const newConfirm = this.state.backup_popup_newpassword_confirm

		if (!oldPassword) { this.setState({ backup_popup_error: "Please enter your current password." }); return }
		if (!newPassword) { this.setState({ backup_popup_error: "Please enter a new password." }); return }
		if (newPassword !== newConfirm) { this.setState({ backup_popup_error: "New password and confirmation do not match." }); return }

		this.setState({ backup_popup_loading: true, backup_popup_error: "" })
		await new Promise(resolve => setTimeout(resolve, 200))

		try {
			const ok = await this.context.verifyWalletPassword(oldPassword)
			if (!ok) {
				this.setState({ backup_popup_loading: false, backup_popup_error: "Current password is wrong." })
				return
			}

			const success = await this.context.change_wallet_password(newPassword)
			if (!success) {
				this.setState({ backup_popup_loading: false, backup_popup_error: "Failed to change password. Please try again." })
				return
			}

			log("password changed successfully")
			this.setState({
				backup_popup_loading: false,
				backup_popup_success: "Password changed. The wallet remembered on this device now uses your new password.",
				backup_popup_password: "",
				backup_popup_newpassword: "",
				backup_popup_newpassword_confirm: "",
			})
		} catch (err) {
			log("change password error:", err)
			this.setState({ backup_popup_loading: false, backup_popup_error: String(err && err.message ? err.message : err) })
		}
	}


	async handleConfirm_show_seed_password() {
		const function_id = this.classname + "handleConfirm_show_seed_password(): "
		const log = getLog(function_id)

		const password = this.state.backup_popup_password
		if (!password) { this.setState({ backup_popup_error: "Please enter your password." }); return }

		this.setState({ backup_popup_loading: true, backup_popup_error: "" })
		await new Promise(resolve => setTimeout(resolve, 200))

		try {
			const ok = await this.context.verifyWalletPassword(password)
			if (!ok) {
				this.setState({ backup_popup_loading: false, backup_popup_error: "Wrong password." })
				return
			}

			log("password verified, revealing mnemonic")
			this.setState({
				backup_popup: null,
				backup_popup_password: "",
				backup_popup_loading: false,
				backup_popup_error: "",
				showmnemonic: true,
				showmnemonic_timeleft: this.showmnemonic_timeshow,
			})
			setTimeout(this.reduce_timeleft, 50)
		} catch (err) {
			log("show seed password verify error:", err)
			this.setState({ backup_popup_loading: false, backup_popup_error: String(err && err.message ? err.message : err) })
		}
	}


	// --- Render ---

	renderBackupPopup() {
		if (!this.state.backup_popup) return null

		const popup = this.state.backup_popup
		const loading = this.state.backup_popup_loading
		const error = this.state.backup_popup_error
		const success = this.state.backup_popup_success

		let title = ""
		let confirmHandler = null
		let showNewPasswordFields = false
		let confirmButtonText = "Confirm"

		if (popup === "export") {
			title = "Export encrypted backup file"
			confirmHandler = this.handleConfirm_export
			confirmButtonText = "Export"
		} else if (popup === "change_password") {
			title = "Change password"
			confirmHandler = this.handleConfirm_change_password
			showNewPasswordFields = true
			confirmButtonText = "Change password"
		} else if (popup === "show_seed_password") {
			title = "Verify password to show seed phrase"
			confirmHandler = this.handleConfirm_show_seed_password
			confirmButtonText = "Reveal seed phrase"
		}

		const isChangePasswordPopup = (popup === "change_password")

		return (
			<SlidePopup
				onButtonClick={this.close_backup_popup}
				buttonText="Cancel"
				hideButton={loading || !!success}
			>
				<h3>{title}</h3><br />

				{success ?
					<Fragment>
						<Alert variant="success" style={{ fontSize: "16px" }}>
							<i className="fa fa-check-circle" style={{ fontSize: "20px" }} /> &nbsp;
							{success}
						</Alert><br />
						{isChangePasswordPopup ?
							<Fragment>
								<Alert variant="warning" style={{ fontSize: "14px", textAlign: "left" }}>
									<i className="fa fa-exclamation-triangle" style={{ fontSize: "18px" }} /> &nbsp;
									Your existing backup file on disk still uses your <b>old</b> password — it was not changed.
									If you want a backup file with your new password, export a fresh one now.
								</Alert><br />
								<ButtonPrimary
									onClick={() => {
										this.setState({ backup_popup: "export", backup_popup_success: "", backup_popup_password: "", backup_popup_error: "", backup_popup_loading: false })
									}}
									style={{ margin: "0.5em" }}
								>
									<i className="fas fa-download" /> &nbsp; Export with new password
								</ButtonPrimary>
								&nbsp;&nbsp;
								<ButtonPrimaryOutlined onClick={this.close_backup_popup}>
									Close
								</ButtonPrimaryOutlined>
							</Fragment>
							:
							<ButtonPrimaryOutlined onClick={this.close_backup_popup}>
								Close
							</ButtonPrimaryOutlined>
						}
					</Fragment>
					:
					<Fragment>
						{isChangePasswordPopup ?
							<Alert variant="warning" style={{ fontSize: "14px", textAlign: "left", marginBottom: "1em" }}>
								<i className="fa fa-exclamation-triangle" style={{ fontSize: "18px" }} /> &nbsp;
								This changes the password for the wallet <b>remembered on this device</b> (your browser session).<br /><br />
								Your existing <b>backup file on disk is not changed</b> — it still uses your current password.<br />
								If you want a backup file with the new password, export a fresh one after changing.
							</Alert>
						: null}
						<div style={{ display: "flex", justifyContent: "center" }}>
							<TextField
								type="password"
								variant="outlined"
								value={this.state.backup_popup_password}
								onChange={this.handleChange_backup_popup_password}
								onKeyDown={(e) => { if (e.key === 'Enter' && !showNewPasswordFields && !loading) confirmHandler() }}
								label={showNewPasswordFields ? "Current password" : "Password"}
								style={{ width: "300px" }}
								error={!!error}
							/>
						</div>

						{showNewPasswordFields ?
							<Fragment>
								<br />
								<div style={{ display: "flex", justifyContent: "center" }}>
									<TextField
										type="password"
										variant="outlined"
										value={this.state.backup_popup_newpassword}
										onChange={this.handleChange_backup_popup_newpassword}
										label="New password"
										style={{ width: "300px" }}
									/>
								</div>
{/* Strength meter for the new password (advisory-only) */}
							<div style={{ width: "300px", margin: "0 auto" }}>
								<PasswordStrengthMeter password={this.state.backup_popup_newpassword} />
							</div>
							{/* Password generator for the new password */}
							<div style={{ width: "300px", margin: "0 auto" }}>
								<PasswordGenerator onGenerate={(pw) => {
									this.setState({
										backup_popup_newpassword: pw,
										backup_popup_newpassword_confirm: "",
										backup_popup_error: "",
									})
								}} />
							</div>
							<div style={{ display: "flex", justifyContent: "center" }}>
									<TextField
										type="password"
										variant="outlined"
										value={this.state.backup_popup_newpassword_confirm}
										onChange={this.handleChange_backup_popup_newpassword_confirm}
										onKeyDown={(e) => { if (e.key === 'Enter' && !loading) confirmHandler() }}
										label="Confirm new password"
										style={{ width: "300px" }}
										error={!!error}
									/>
								</div>
							</Fragment>
						: null}

						{error ?
							<Fragment><br />
								<Alert variant="danger" style={{ fontSize: "14px" }}>{error}</Alert>
							</Fragment>
						: null}

						{loading ?
							<Fragment><br />
								<div style={{ textAlign: "center" }}><Loader small /></div>
							</Fragment>
						: null}

						{!loading ?
							<Fragment><br />
								<ButtonPrimary
									onClick={confirmHandler}
									disabled={!this.state.backup_popup_password}
									style={{
										pointerEvents: this.state.backup_popup_password ? "auto" : "none",
										backgroundColor: this.state.backup_popup_password ? "" : "gray",
									}}
								>
									{confirmButtonText}
								</ButtonPrimary>
							</Fragment>
						: null}
					</Fragment>
				}
			</SlidePopup>
		)
	}


	render() {
		// seed phrase button or revealed mnemonic
		let mnemonic_or_button = (
			<ButtonParam onClick={this.handleClick_showmnemonic}>
				Show seed phrase
			</ButtonParam>
		)

		const isMockWallet = this.context.isMockWallet || false
		if (isMockWallet) {
			mnemonic_or_button = <div>This is an example wallet, create a new wallet to show the seed phrase.</div>
		}

		if (this.state.showmnemonic) {
			mnemonic_or_button = (
				<div style={{ padding: "15px", textAlign: "center", border: "1px solid red" }}>
					<TextCopyQR value={this.context.get_mnemonic_or_devmode()} />
				</div>
			)
		}

		return (
			<Fragment>
				<ClickToOpenCardDark title="Backup & Password">
					<center><br />
						{/* --- Seed phrase section --- */}
						<Alert variant="danger" style={{ fontWeight: "bold", fontSize: "18px", margin: "0" }}>
							<i className="fa fa-info-circle" style={{ fontSize: "20px" }} /> &nbsp;
							{Constants.SEED_INFO}
						</Alert><br />

						<Alert variant="danger" style={{ fontWeight: "bold", fontSize: "18px", margin: "0" }}>
							<i className="fa fa-info-circle" style={{ fontSize: "20px" }} /> &nbsp;
							{Constants.SEED_INFO_MORE}
						</Alert><br />

						<div style={{ width: "100%" }}>
							{mnemonic_or_button}
							{this.state.showmnemonic ?
								<ProgressBar animated striped variant="danger" style={{ height: "0.5em", borderRadius: "0" }}
									now={this.state.showmnemonic_timeleft / this.showmnemonic_timeshow * 100} />
								: null}
						</div>
						<br /><br />

						{/* --- Separator --- */}
						<div style={{ width: "100%", borderBottom: "1px solid #d2691e", opacity: 0.4, margin: "1em 0" }} />

						{/* --- Export & change password section (hidden for mock wallets) --- */}
						{isMockWallet ? null : (
							<Fragment>
								<Alert variant="info" style={{ fontSize: "16px", margin: "0", textAlign: "left" }}>
									<i className="fa fa-info-circle" style={{ fontSize: "20px" }} /> &nbsp;
									Export a fresh encrypted backup file to disk, or change your wallet password.
									Both actions require your current password.
								</Alert><br />

								<ButtonParam onClick={() => this.open_backup_popup("export")} style={{ margin: "0.5em" }}>
									<i className="fas fa-download" /> &nbsp; Export encrypted backup file
								</ButtonParam><br /><br />

								<ButtonParam onClick={() => this.open_backup_popup("change_password")} style={{ margin: "0.5em" }}>
									<i className="fas fa-key" /> &nbsp; Change password
								</ButtonParam>
							</Fragment>
						)}
						<br />
					</center>
				</ClickToOpenCardDark>
				<br />

				{this.renderBackupPopup()}
			</Fragment>
		)
	}
}

export default SectionBackupPassword
